import React, {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    startTransition,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { ModalPayloads, Modals, ModalTypes } from './types'
import { noop } from '~utils/noop'

type SetModals = Dispatch<SetStateAction<Modals>>

type ModalsContextType = {
    modals: Modals
    setModals: SetModals
}

const defaultValue = { modals: [], setModals: noop }
export const ModalsContext = createContext<ModalsContextType>(defaultValue)

/**
 * ModalsContextLayer
 */
export function ModalsContextLayer({ children }: { children: ReactNode }) {
    const [modals, setModals] = useState<Modals>([])
    const location = useLocation()

    useEffect(() => {
        return () => {
            startTransition(() => {
                setModals([])
            })
        }
    }, [location.pathname, setModals])

    const contextValue = useMemo(
        () => ({
            modals,
            setModals,
        }),
        [modals]
    )

    return <ModalsContext.Provider value={contextValue}>{children}</ModalsContext.Provider>
}

export const useModal = <T extends ModalTypes>(type: T) => {
    const { modals, setModals } = useContext(ModalsContext)

    const modal = useMemo(() => modals.find(item => item.type === type), [modals, type])
    const data = modal?.data as ModalPayloads[T]

    const show = useCallback(
        (data?: ModalPayloads[T]) =>
            startTransition(() =>
                setModals(prevModals => [
                    ...prevModals.filter(item => item.type !== type),
                    { type, data, isOpen: true },
                ])
            ),
        [setModals, type]
    )

    const setData = useCallback(
        (data?: ModalPayloads[T]) => {
            startTransition(() =>
                setModals(prevModals => {
                    const currentModal = prevModals.find(item => item.type === type)
                    if (!currentModal) {
                        return prevModals
                    }

                    const newModals = prevModals.filter(item => item.type !== type)

                    return [...newModals, { ...currentModal, data }]
                })
            )
        },
        [setModals, type]
    )

    const hide = useCallback(
        () => startTransition(() => setModals(prevModals => prevModals.filter(item => item.type !== type))),
        [setModals, type]
    )

    return { show, hide, setData, isOpen: modal?.isOpen ?? false, data }
}
